import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { X, MapPin, Search, BadgeCheck, ArrowRight, Music2, Building2, Flame, TrendingUp } from 'lucide-react';
import api from '../lib/api';

/**
 * Mobile drawer with two tabs (Productoras / DJs) listing the public community
 * of the platform. Triggered from MobileBottomNav. Each card links to the
 * existing public detail page (`/productora/:slug` or `/dj/:slug`).
 */
const CommunityDrawer = ({ open, onClose }) => {
  const [tab, setTab] = useState('producers');
  const [search, setSearch] = useState('');
  const [producers, setProducers] = useState(null);
  const [djs, setDjs] = useState(null);

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    (async () => {
      try {
        const [pRes, dRes] = await Promise.all([
          api.get('/public/producers').catch(() => ({ data: [] })),
          api.get('/public/djs').catch(() => ({ data: [] })),
        ]);
        if (!mounted) return;
        setProducers(Array.isArray(pRes.data) ? pRes.data : []);
        setDjs(Array.isArray(dRes.data) ? dRes.data : []);
      } catch (_) {}
    })();
    return () => { mounted = false; };
  }, [open]);

  // Lock body + ESC to close
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  const list = tab === 'producers' ? producers : djs;
  const filtered = (list || []).filter((x) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      (x.name || '').toLowerCase().includes(s) ||
      (x.city || '').toLowerCase().includes(s) ||
      (x.slug || '').toLowerCase().includes(s)
    );
  });

  if (!open) return null;

  // Top of the week — first item in each (already sorted server-side).
  const topProducer = (producers || []).find(p => p.upcoming_events_count > 0) || (producers || [])[0];
  const topDj = (djs || []).find(d => d.profile_views > 0) || (djs || [])[0];

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="community-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm md:flex md:items-center md:justify-center md:p-6"
        onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        data-testid="community-drawer"
      >
        <motion.div
          key="community-sheet"
          initial={{ y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{ type: 'spring', damping: 35, stiffness: 350 }}
          className="absolute bottom-0 left-0 right-0 bg-[#0A0A0A] border-t border-white/10 rounded-t-3xl shadow-2xl
                     md:relative md:bottom-auto md:rounded-3xl md:border md:max-w-2xl md:w-full md:max-h-[88vh]"
          style={{ maxHeight: '88vh', display: 'flex', flexDirection: 'column' }}
        >
          {/* Grab handle (mobile only) */}
          <div className="pt-3 pb-2 flex justify-center md:hidden">
            <div className="w-12 h-1.5 bg-white/20 rounded-full" />
          </div>

          {/* Header */}
          <div className="px-5 md:px-6 pt-3 md:pt-6 pb-3 flex items-center justify-between">
            <div>
              <h2 className="text-xl md:text-2xl font-black text-white tracking-tighter">Comunidad Bravo</h2>
              <p className="text-xs text-zinc-500">Productoras y DJs en la plataforma</p>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-400 hover:text-white transition-all"
              data-testid="community-drawer-close"
            >
              <X size={18} />
            </button>
          </div>

          {/* Top of the week */}
          {(topProducer || topDj) && (
            <div className="px-5 md:px-6 mb-4" data-testid="community-top-of-week">
              <div className="flex items-center gap-2 mb-2">
                <Flame size={12} className="text-[#D4AF37]" />
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4AF37]">
                  Top de la semana
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                {topProducer && (
                  <Link
                    to={`/productora/${topProducer.slug || topProducer.id}`}
                    onClick={onClose}
                    data-testid="community-top-producer"
                    className="relative rounded-2xl overflow-hidden group bg-zinc-900 border border-white/10 hover:border-[#D4AF37] transition-all"
                  >
                    <div className="aspect-[4/5] w-full relative">
                      {topProducer.cover || topProducer.logo ? (
                        <img
                          src={topProducer.cover || topProducer.logo}
                          alt={topProducer.name}
                          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div
                          className="absolute inset-0"
                          style={{ background: `linear-gradient(135deg, ${topProducer.color || '#D4AF37'}40, #000)` }}
                        />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
                      <div className="absolute top-2 left-2">
                        <span className="px-2 py-0.5 rounded-full bg-[#D4AF37] text-black text-[9px] font-black uppercase tracking-wider">
                          #1 Productora
                        </span>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <div className="flex items-center gap-1 mb-1">
                          <p className="font-black text-white text-sm truncate">{topProducer.name}</p>
                          {topProducer.is_verified && <BadgeCheck size={12} className="text-blue-400 flex-shrink-0" />}
                        </div>
                        {topProducer.upcoming_events_count > 0 && (
                          <p className="text-[10px] text-emerald-400 font-bold uppercase tracking-wider flex items-center gap-1">
                            <Flame size={10} />
                            {topProducer.upcoming_events_count} eventos
                          </p>
                        )}
                      </div>
                    </div>
                  </Link>
                )}
                {topDj && (
                  <Link
                    to={`/dj/${topDj.slug || topDj.id}`}
                    onClick={onClose}
                    data-testid="community-top-dj"
                    className="relative rounded-2xl overflow-hidden group bg-zinc-900 border border-white/10 hover:border-fuchsia-500 transition-all"
                  >
                    <div className="aspect-[4/5] w-full relative">
                      {topDj.banner || topDj.photo ? (
                        <img
                          src={topDj.banner || topDj.photo}
                          alt={topDj.name}
                          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-700/30 to-zinc-900" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
                      <div className="absolute top-2 left-2">
                        <span className="px-2 py-0.5 rounded-full bg-fuchsia-500 text-white text-[9px] font-black uppercase tracking-wider">
                          #1 DJ
                        </span>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <p className="font-black text-white text-sm truncate mb-1">{topDj.name}</p>
                        {topDj.genres?.length > 0 && (
                          <p className="text-[10px] text-zinc-300 truncate">{topDj.genres.join(' · ')}</p>
                        )}
                        {topDj.profile_views > 0 && (
                          <p className="text-[10px] text-fuchsia-300 font-bold uppercase tracking-wider flex items-center gap-1 mt-0.5">
                            <TrendingUp size={10} />
                            {topDj.profile_views} visitas
                          </p>
                        )}
                      </div>
                    </div>
                  </Link>
                )}
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="px-5 md:px-6 mb-3 flex items-center gap-2">
            <button
              onClick={() => setTab('producers')}
              data-testid="community-tab-producers"
              className={`flex-1 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-2 ${
                tab === 'producers'
                  ? 'bg-[#D4AF37] text-black shadow-lg shadow-yellow-500/20'
                  : 'bg-white/5 text-zinc-400 hover:text-white'
              }`}
            >
              <Building2 size={14} />
              Productoras
              {producers && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${tab === 'producers' ? 'bg-black/20' : 'bg-white/10'}`}>
                  {producers.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setTab('djs')}
              data-testid="community-tab-djs"
              className={`flex-1 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-2 ${
                tab === 'djs'
                  ? 'bg-[#D4AF37] text-black shadow-lg shadow-yellow-500/20'
                  : 'bg-white/5 text-zinc-400 hover:text-white'
              }`}
            >
              <Music2 size={14} />
              DJs
              {djs && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${tab === 'djs' ? 'bg-black/20' : 'bg-white/10'}`}>
                  {djs.length}
                </span>
              )}
            </button>
          </div>

          {/* Search */}
          <div className="px-5 md:px-6 mb-3 relative">
            <Search className="absolute left-8 md:left-9 top-1/2 -translate-y-1/2 text-zinc-500" size={14} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={tab === 'producers' ? 'Buscar productora...' : 'Buscar DJ...'}
              className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#D4AF37]/40"
              data-testid="community-drawer-search"
            />
          </div>

          {/* List */}
          <div
            className="px-5 md:px-6 pb-8 overflow-y-auto"
            style={{ flex: 1, paddingBottom: 'calc(env(safe-area-inset-bottom) + 24px)' }}
          >
            {list === null ? (
              <div className="py-10 text-center text-zinc-500 text-sm">Cargando...</div>
            ) : filtered.length === 0 ? (
              <div className="py-10 text-center text-zinc-500 text-sm">
                {search ? 'Sin resultados.' : 'Aún no hay registros.'}
              </div>
            ) : (
              <ul className="space-y-2" data-testid="community-drawer-list">
                {filtered.map((item) => {
                  const href = tab === 'producers'
                    ? `/productora/${item.slug || item.id}`
                    : `/dj/${item.slug || item.id}`;
                  const initials = (item.name || '?').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
                  const imgUrl = tab === 'producers' ? item.logo : item.photo;
                  return (
                    <li key={item.id || item.slug}>
                      <Link
                        to={href}
                        onClick={onClose}
                        className="flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#D4AF37]/30 rounded-xl transition-all group"
                        data-testid={`community-item-${tab}-${item.slug || item.id}`}
                      >
                        {/* Avatar */}
                        <div
                          className="w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center font-bold text-sm overflow-hidden border-2"
                          style={{
                            background: imgUrl ? 'transparent' : `${item.color || '#D4AF37'}22`,
                            borderColor: item.color || '#D4AF37',
                            color: item.color || '#D4AF37',
                          }}
                        >
                          {imgUrl ? (
                            <img src={imgUrl} alt={item.name} className="w-full h-full object-cover" />
                          ) : (
                            <span>{initials}</span>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                            <p className="font-bold text-white truncate">{item.name || 'Sin nombre'}</p>
                            {tab === 'producers' && item.is_verified && (
                              <BadgeCheck size={14} className="text-blue-400 flex-shrink-0" />
                            )}
                            {tab === 'producers' && item.upcoming_events_count > 0 && (
                              <span
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[9px] font-bold uppercase tracking-wider flex-shrink-0"
                                data-testid={`community-active-badge-${item.slug || item.id}`}
                                title={`${item.upcoming_events_count} eventos próximos`}
                              >
                                <Flame size={9} />
                                Activa · {item.upcoming_events_count}
                              </span>
                            )}
                            {tab === 'djs' && item.profile_views >= 50 && (
                              <span
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-fuchsia-500/15 border border-fuchsia-500/40 text-fuchsia-300 text-[9px] font-bold uppercase tracking-wider flex-shrink-0"
                                data-testid={`community-trending-badge-${item.slug || item.id}`}
                                title={`${item.profile_views} visitas al perfil`}
                              >
                                <TrendingUp size={9} />
                                Trending
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 text-[11px] text-zinc-500">
                            {item.city && (
                              <span className="flex items-center gap-1">
                                <MapPin size={10} />
                                {item.city}
                                {item.country ? `, ${item.country}` : ''}
                              </span>
                            )}
                            {tab === 'djs' && item.genres?.length > 0 && (
                              <>
                                {item.city && <span className="text-zinc-700">·</span>}
                                <span className="truncate">{item.genres.join(' · ')}</span>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Arrow */}
                        <ArrowRight size={16} className="text-zinc-600 group-hover:text-[#D4AF37] transition-colors flex-shrink-0" />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default CommunityDrawer;
